import { Theme, useTheme } from "@deere/fuel-react";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import NorthIcon from "@mui/icons-material/North";
import SouthIcon from "@mui/icons-material/South";
import { Box, Button, Typography } from "@mui/material";
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  GridRenderHeaderFilterProps,
  GridSortDirection,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarContainer,
} from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import { TFunction } from "i18next";
import { Dispatch, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DownloadButton from "../../../../../components/DataGridPro/DownloadButton/DownloadButton/DownloadButton";
import Link from "../../../../../components/Link/Link";
import LoadingIndicator from "../../../../../components/LoadingIndicator/LoadingIndicator";
import { generateDataGridLocaleTexts } from "../../../../../utils/dataGridUtils/utils";
import { D2DOrdersActions } from "../../reducers/actions";
import { D2DOrdersFilters } from "../../types";
import { getStatusBadge } from "../../utils";
import { GroupedD2DOrder } from "../../utils/orderGrouping";
import { generateGridDefaultStyles, reorderColumns } from "../../utils/table";
import D2DOrderDatePicker from "../D2DOrderDatePicker/D2DOrderDatePicker";
import D2DOrdersSearchFilter from "../D2DOrdersSearchFilter/D2DOrdersSearchFilter";
import D2DOrderStatusFilter from "../D2DOrderStatusFilter/D2DOrderStatusFilter";

interface ColumnGenerationConfig {
  theme: Theme;
  t: TFunction;
}

interface ColumnGenerationState {
  filter: D2DOrdersFilters;
  dispatch: Dispatch<D2DOrdersActions>;
}

const formatDateCell = (dateStr: string | undefined | null) => {
  if (!dateStr) return { dateLine: "", timeLine: "" };
  let d = dayjs(dateStr);
  if (!d.isValid()) d = dayjs(new Date(dateStr));
  if (!d.isValid()) return { dateLine: "", timeLine: "" };
  return {
    dateLine: d.format("DD-MMM-YYYY"),
    timeLine: d.format("ddd hh:mm A"),
  };
};

const generateColumns = (
  config: ColumnGenerationConfig,
  state: ColumnGenerationState
): GridColDef[] => {
  const { theme, t } = config;
  const { filter, dispatch } = state;

  return (
    [
      {
        field: "orderNumber",
        headerName: t("d2d:d2dOrders.table.header.orderNumber"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => {
          const orderNumber = params.value as string;
          const accountId = params.row.accountId || params.row.sellerAccountId;
          return (
            <Link
              to={`/dealer/${accountId}/orders/${orderNumber}`}
              data-testid="d2d-order-number-link"
            >
              <Typography
                sx={{
                  color: theme.tokens.FuelColorTextBrandPrimary,
                  fontWeight: theme.tokens.FuelFontWeightRegular,
                  fontSize: "14px",
                  textDecoration: "underLine",
                  ":hover": {
                    cursor: "pointer",
                    color: theme.tokens.FuelColorFocused,
                  },
                }}
              >
                {orderNumber}
              </Typography>
            </Link>
          );
        },
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="orderNumber"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      {
        field: "dealerReference",
        headerName: t("d2d:d2dOrders.table.header.dealerReference"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => params.value || "--",
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="dealerReference"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      {
        field: "partNumber",
        headerName: t("d2d:d2dOrders.table.header.partNumber"),
        filterable: true,
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="partNumber"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      {
        field: "requestedQuantity",
        headerName: t("d2d:d2dOrders.table.header.requestedQuantity"),
        type: "number",
        filterable: false,
        renderHeaderFilter: () => <></>,
        minWidth: 150,
      },
      {
        field: "acceptedQuantity",
        headerName: t("d2d:d2dOrders.table.header.acceptedQuantity"),
        type: "number",
        filterable: false,
        renderHeaderFilter: () => <></>,
        renderCell: (params: GridRenderCellParams) => {
          const { value } = params;
          if (value === null || value === undefined || value === 0) {
            return "--";
          }
          return value;
        },
        minWidth: 150,
      },
      {
        field: "sellerAccountId",
        headerName: t("d2d:d2dOrders.table.header.sellerAccountId"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => {
          const { value } = params;
          if (!value || value === "") {
            return "--";
          }
          return value;
        },
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="sellerAccountId"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 150,
      },
      {
        field: "dateRequested",
        headerName: t("d2d:d2dOrders.table.header.dateRequested"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => {
          const { dateLine, timeLine } = formatDateCell(params.value as string);
          return (
            <Box>
              <Typography variant="body2">{dateLine}</Typography>
              <Typography variant="caption" color="text.secondary">
                {timeLine}
              </Typography>
            </Box>
          );
        },
        renderHeaderFilter: () => (
          <D2DOrderDatePicker filters={filter} dispatch={dispatch} />
        ),
        minWidth: 190,
      },
      {
        field: "carrierName",
        headerName: t("d2d:d2dOrders.table.header.carrierName"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => params.value || "--",
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="carrierName"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      {
        field: "trackingNumber",
        headerName: t("d2d:d2dOrders.table.header.trackingNumber"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) => params.value || "--",
        renderHeaderFilter: ({ inputRef }: GridRenderHeaderFilterProps) => (
          <D2DOrdersSearchFilter
            inputRef={inputRef}
            field="trackingNumber"
            filters={filter}
            dispatch={dispatch}
          />
        ),
        minWidth: 175,
      },
      {
        field: "status",
        headerName: t("d2d:d2dOrders.table.header.status"),
        filterable: true,
        renderCell: (params: GridRenderCellParams) =>
          getStatusBadge(params.value as string, t, {
            lastChangedBy: params.row.lastChangedBy,
            rejectionType: params.row.rejectionType,
            rejectionTrigger: params.row.rejectionTrigger,
          }),
        renderHeaderFilter: () => (
          <D2DOrderStatusFilter filters={filter} dispatch={dispatch} />
        ),
        minWidth: 200,
      },
    ] as GridColDef[]
  ).map((column) => ({
    headerAlign: "left",
    align: "left",
    minWidth: 150,
    flex: 1,
    ...column,
  }));
};

export interface D2DOrdersTableV2Props {
  orders: GroupedD2DOrder[];
  loading: boolean;
  showTechnicalError: boolean;
  sort: {
    sortModel: GridSortModel;
    updateSortModel: (newSortModel: GridSortModel) => void;
  };
  filter: {
    d2dOrdersFilters: D2DOrdersFilters;
    isFilterApplied: boolean;
    onClearFilters?: () => void;
  };
  download: {
    isDownloading: boolean;
    onDownload: () => void;
    tooltipText: string;
  };
  dispatch: Dispatch<D2DOrdersActions>;
}

const D2DOrdersTableV2 = ({
  orders,
  loading,
  showTechnicalError,
  sort,
  filter,
  download,
  dispatch,
}: D2DOrdersTableV2Props) => {
  const { sortModel } = sort;
  const theme = useTheme();
  const { t } = useTranslation(["common", "d2d", "orders"]);

  const createColumns = () =>
    generateColumns(
      { theme, t },
      { filter: filter.d2dOrdersFilters, dispatch }
    );

  const [columns, setColumns] = useState(createColumns());

  useEffect(() => {
    setColumns(createColumns);
  }, [filter]);

  useEffect(() => {
    setColumns((prev) =>
      prev.map((col) => ({
        ...col,
        sortable: !loading && (col.sortable ?? true),
      }))
    );
  }, [loading]);

  const getNextSortDirection = (
    currentDirection: GridSortDirection | undefined
  ): GridSortDirection | undefined => {
    if (!currentDirection) return "asc";
    if (currentDirection === "asc") return "desc";
    return undefined;
  };

  const handleColumnHeaderClick = (params: { field: any }) => {
    const { field } = params;
    if (loading) return;

    if (!field) return;
    const currentSort = sortModel.find((s) => s.field === field);
    const nextDirection = getNextSortDirection(currentSort?.sort);
    let newSortModel: GridSortModel = [];
    if (nextDirection) {
      newSortModel = [{ field, sort: nextDirection }];
    }
    sort.updateSortModel(newSortModel);
  };

  const hiddenFields = ["__tree_data_group__"];

  const getTogglableColumns = (gridColumns: GridColDef[]) =>
    gridColumns
      .filter((column) => !hiddenFields?.includes(column.field))
      .map((column) => column.field);

  const getRows = () => {
    if (loading) return [];
    return orders;
  };

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <DataGridPro
        localeText={generateDataGridLocaleTexts(t)}
        data-testid="d2d-orders-table"
        sx={{ ...generateGridDefaultStyles(theme), height: "auto" }}
        style={{ borderRadius: "10px" }}
        columnBufferPx={columns.length + 1}
        rows={getRows()}
        columns={columns}
        hideFooter
        getRowHeight={() => "auto"}
        headerFilters={filter.isFilterApplied || orders.length > -1}
        headerFilterHeight={60}
        loading={loading}
        disableColumnMenu
        disableRowSelectionOnClick
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(newModel) => {
          if (loading) return;
          sort.updateSortModel(newModel);
        }}
        onColumnOrderChange={({ targetIndex, oldIndex }) =>
          setColumns((prevState) =>
            reorderColumns(prevState, oldIndex - 1, targetIndex - 1)
          )
        }
        onColumnHeaderClick={handleColumnHeaderClick}
        slots={{
          columnUnsortedIcon: ImportExportIcon,
          columnSortedAscendingIcon: NorthIcon,
          columnSortedDescendingIcon: SouthIcon,
          loadingOverlay: LoadingIndicator,
          // eslint-disable-next-line react/no-unstable-nested-components
          toolbar: () => (
            <GridToolbarContainer
              sx={{
                paddingX: theme.tokens.FuelSpacing2,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.tokens.FuelSpacing2,
                }}
              >
                <GridToolbarColumnsButton />
                <Button
                  variant="text"
                  onClick={filter.onClearFilters ?? (() => undefined)}
                  disabled={!filter.isFilterApplied}
                  data-testid="d2d-orders-clear-filters"
                  sx={{
                    paddingX: 0,
                    textTransform: "none",
                    fontWeight: theme.tokens.FuelFontWeightSemibold,
                    fontSize: theme.tokens.FuelFontSizeSm,
                  }}
                >
                  {t("orders:allOrders.table.filters.clearButton")}
                </Button>
              </Box>
              <DownloadButton
                disabled={
                  download.isDownloading ||
                  (orders.length === 0 && !filter.isFilterApplied)
                }
                onClick={download.onDownload}
                tooltipText={download.tooltipText}
                testId="d2d-orders-download-button"
              />
            </GridToolbarContainer>
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          noRowsOverlay: () => (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography variant="body1">
                {showTechnicalError
                  ? t("common:technicalError.subtitle")
                  : t("common:error.noDataAvailable")}
              </Typography>
            </Box>
          ),
        }}
        slotProps={{
          columnsManagement: { getTogglableColumns },
        }}
      />
    </Box>
  );
};

export default D2DOrdersTableV2;
